import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  CircularProgress,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  Help,
  Send,
  Mail,
  LocationOn,
  Home,
  Info,
  CheckCircle,
  Error as ErrorIcon,
  Person,
  ContactMail,
  DataObject,
  Public,
  School,
  Warning,
  Feedback,
  ArrowBack,
  Description,
  Language,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import HomeRefreshButton from "../buttons/HomeRefreshButton";
import {
  submitFeedback,
  resetFeedback,
  selectFeedbackLoading,
  selectFeedbackError,
  selectFeedbackSuccess,
} from "../../store/slices/feedbackSlice";
import {
  quickSearchChitalishta,
  selectQuickSearchResults,
  selectQuickSearchLoading,
} from "../../store/slices/chitalishtaSlice";
import { debounce } from "lodash";

const FeedbackComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery("(max-width:600px)");

  const dispatch = useDispatch();
  const loading = useSelector(selectFeedbackLoading);
  const error = useSelector(selectFeedbackError);
  const success = useSelector(selectFeedbackSuccess);

  // Autocomplete state
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const searchResults = useSelector(selectQuickSearchResults);
  const searchLoading = useSelector(selectQuickSearchLoading);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    feedbackType: "",
    chitalishteName: "",
    chitalishteId: null,
    locationDetails: "",
    additionalInfo: "",
    websiteInfo: "",
    message: "",
    agreeTerms: false,
    contactPermission: false,
  });

  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        dispatch(quickSearchChitalishta(query));
      }, 300),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);


  const feedbackTypes = [
    {
      value: "location",
      label: "Обратна връзка по местоположението на читалището",
      icon: <LocationOn />,
      description: "Сигнализиране за неточно географско местоположение",
      color: "#1976d2",
    },
    {
      value: "basic_info",
      label:
        "Обратна връзка по адрес, контакти, име, година на основаване и други ключови данни",
      icon: <Home />,
      description: "Корекция на основни данни за читалището",
      color: "#2e7d32",
    },
    {
      value: "activity",
      label: "Обратна връзка по данните за дейността на читалището",
      icon: <School />,
      description: "Информация за културни дейности, проекти и инициативи",
      color: "#ed6c02",
    },
    {
      value: "website",
      label: "Искам да посоча сайт или Facebook страница на читалището",
      icon: <Public />,
      description: "Добавяне на онлайн присъствие на читалището",
      color: "#9c27b0",
    },
    {
      value: "other",
      label: "Друга обратна връзка",
      icon: <Feedback />,
      description: "Общи предложения и коментари",
      color: "#757575",
    },
  ];

  // Responsive spacing values
  const getSpacing = () => {
    if (isSmall) return 2;
    if (isMobile) return 3;
    return 4;
  };

  // Responsive typography variants
  const getTitleVariant = () => {
    if (isSmall) return "h4";
    if (isMobile) return "h3";
    return "h3";
  };

  useEffect(() => {
    if (success) {
      setSnackbar({
        open: true,
        message:
          "Благодарим ви за обратната връзка! Вашият сигнал е изпратен успешно.",
        severity: "success",
      });

      setFormData({
        name: "",
        email: "",
        feedbackType: "",
        chitalishteName: "",
        chitalishteId: null,
        locationDetails: "",
        additionalInfo: "",
        websiteInfo: "",
        message: "",
        agreeTerms: false,
        contactPermission: false,
      });

      setTimeout(() => {
        dispatch(resetFeedback());
      }, 5000);
    }

    if (error) {
      let errorMessage =
        "Възникна грешка при изпращане. Моля, опитайте отново.";

      if (error.message) {
        errorMessage = error.message;
      }

      if (error.status === 0) {
        errorMessage =
          "Няма връзка със сървъра. Моля, проверете интернет връзката.";
      } else if (error.status === 400) {
        errorMessage = "Моля, попълнете формата правилно.";
      } else if (error.status === 500) {
        errorMessage = "Възникна сървърна грешка. Моля, опитайте по-късно.";
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  }, [success, error, dispatch]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.feedbackType) {
      newErrors.feedbackType = "Моля, изберете категория на обратната връзка";
    }

    if (!formData.chitalishteId && !formData.chitalishteName) {
        newErrors.chitalishteName = "Моля, изберете читалище от списъка";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Моля, опишете подробно вашата обратна връзка";
    } else if (formData.message.length < 20) {
      newErrors.message = "Описанието трябва да е поне 20 символа";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Моля, въведете валиден имейл адрес";
    }

    if (!formData.agreeTerms) {
      newErrors.agreeTerms =
        "Моля, съгласете се с изявлението за източник на данните";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // console.log("Submitting form data:", formData);
    dispatch(submitFeedback(formData));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
    if (error || success) {
      dispatch(resetFeedback());
    }
  };

  // Get selected feedback type
  const selectedFeedbackType = feedbackTypes.find(
    (type) => type.value === formData.feedbackType
  );

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 4, sm: 5, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header with back button */}
          {/* <Box sx={{ mb: { xs: 3, md: 4 } }}>
            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBack />}
              sx={{
                mb: 2,
                color: 'text.secondary'
              }}
            >
              Назад към картата
            </Button>
          </Box> */}

          {/* Заглавие */}
          <Box
            sx={{
              textAlign: "center",
              mb: { xs: 4, md: 6 },
              px: { xs: 1, sm: 2 },
            }}
          >
            <Feedback
              sx={{
                fontSize: { xs: 48, sm: 56, md: 60 },
                color: "#1976d2",
                mb: 2,
              }}
            />

            <Typography
              variant={getTitleVariant()}
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "#1a237e",
                mb: 3,
                lineHeight: 1.2,
                wordBreak: "break-word",
                hyphens: "auto",
              }}
            >
              Обратна връзка
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                color: "text.secondary",
                mb: 3,
                maxWidth: "800px",
                mx: "auto",
                textAlign: "justify",
                textAlignLast: { xs: "left", sm: "center" },
              }}
            >
              Вашият принос е важен за точността на данните. Съвместно да
              поддържаме картата актуална и полезна за всички.
            </Typography>

            <Divider
              sx={{
                my: { xs: 3, md: 4 },
                mx: "auto",
                width: { xs: "80px", md: "100px" },
                borderWidth: 2,
                borderColor: "#1976d2",
              }}
            />
          </Box>

          {/* Важна информация */}
          <Card
            elevation={2}
            sx={{
              mb: { xs: 4, md: 6 },
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Alert
                severity="info"
                icon={<Info />}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    fontSize: { xs: 24, md: 30 },
                    alignItems: "center",
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    textAlign: "justify",
                    textAlignLast: "left",
                  }}
                >
                  <strong>Това е версия 1.0</strong> на Първата онлайн карта на
                  народните читалища в България. Въпреки положените усилия за
                  проверка и обработка на данните, е възможно да има неточности
                  или непълна информация.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: "justify",
                    textAlignLast: "left",
                  }}
                >
                  Всички данни в картата са извлечени от{" "}
                  <strong>
                    Публичния регистър на народните читалища и на читалищните
                    сдружения
                  </strong>
                  , поддържан от Министерството на културата, затова ви молим
                  при установяване на липси или грешки първо да проверите дали
                  същите се съдържат и в регистъра.
                </Typography>
              </Alert>

              <Stack spacing={1.5} sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <CheckCircle
                    sx={{
                      color: "#2e7d32",
                      mt: 0.25,
                      fontSize: { xs: 18, md: 20 },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#424242",
                      textAlign: "justify",
                      textAlignLast: "left",
                    }}
                  >
                    Насърчаваме обратната връзка от граждани, читалища и
                    организации, които използват картата.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <CheckCircle
                    sx={{
                      color: "#2e7d32",
                      mt: 0.25,
                      fontSize: { xs: 18, md: 20 },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#424242",
                      textAlign: "justify",
                      textAlignLast: "left",
                    }}
                  >
                    Ако разполагате с по-актуална или допълнителна информация,
                    забележите грешен адрес, местоположение или други
                    неточности, можете да ни сигнализирате чрез този раздел.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Основна форма */}
          <Grid container spacing={getSpacing()}>
            {/* Лева колона - форма */}
            <Grid item xs={12} lg={8}>
              <Card
                elevation={3}
                sx={{
                  borderRadius: 3,
                  height: "100%",
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, sm: 3.5, md: 4 } }}>
                  <form onSubmit={handleSubmit}>
                    <Stack spacing={getSpacing()}>
                      {/* Лична информация */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            color: "#1a237e",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Person />
                          Лична информация
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Вашите имена"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              helperText="Незадължително"
                              size={isSmall ? "small" : "medium"}
                              InputProps={{
                                startAdornment: (
                                  <Person
                                    sx={{ mr: 1, color: "action.active" }}
                                  />
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Имейл адрес"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              error={!!errors.email}
                              helperText={
                                errors.email ||
                                "За обратна връзка относно вашия сигнал"
                              }
                              size={isSmall ? "small" : "medium"}
                              InputProps={{
                                startAdornment: (
                                  <Mail
                                    sx={{ mr: 1, color: "action.active" }}
                                  />
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Категория на обратната връзка */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            color: "#1a237e",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <ContactMail />
                          Категория на обратната връзка *
                        </Typography>

                        <FormControl fullWidth error={!!errors.feedbackType}>
                          <InputLabel size={isSmall ? "small" : "medium"}>
                            Изберете категория
                          </InputLabel>
                          <Select
                            name="feedbackType"
                            value={formData.feedbackType}
                            onChange={handleChange}
                            label="Изберете категория"
                            size={isSmall ? "small" : "medium"}
                            renderValue={(selected) => {
                              const selectedType = feedbackTypes.find(
                                (type) => type.value === selected
                              );
                              return selectedType ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {selectedType.icon}
                                  <Typography
                                    sx={{
                                      fontSize: isSmall ? "0.875rem" : "1rem",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {selectedType.label}
                                  </Typography>
                                </Box>
                              ) : (
                                ""
                              );
                            }}
                          >
                            {feedbackTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 2,
                                    py: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      color: type.color,
                                      mt: 0.5,
                                    }}
                                  >
                                    {type.icon}
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500, mb: 0.5 }}
                                    >
                                      {type.label}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "text.secondary" }}
                                    >
                                      {type.description}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.feedbackType && (
                            <FormHelperText
                              sx={{
                                textAlign: "justify",
                                ml: 0,
                              }}
                            >
                              {errors.feedbackType}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Box>

                      {/* Информация за читалището */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            color: "#1a237e",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Home />
                          Информация за читалището *
                        </Typography>

                        <Stack spacing={2}>
                          <Autocomplete
                            filterOptions={(x) => x}
                            open={autocompleteOpen}
                            onOpen={() => setAutocompleteOpen(true)}
                            onClose={() => setAutocompleteOpen(false)}
                            onChange={(event, newValue) => {
                              setFormData((prev) => ({
                                ...prev,
                                chitalishteId: newValue ? newValue.id : null,
                                chitalishteName: newValue ? newValue.name : "",
                              }));
                              if (errors.chitalishteName) {
                                setErrors((prev) => ({ ...prev, chitalishteName: "" }));
                              }
                            }}
                            onInputChange={(event, newInputValue) => {
                              debouncedSearch(newInputValue);
                            }}
                            options={searchResults}
                            loading={searchLoading}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                <Grid container alignItems="center">
                                  <Grid item sx={{ display: "flex", width: 44 }}>
                                    <School color="primary" />
                                  </Grid>
                                  <Grid
                                    item
                                    sx={{
                                      width: "calc(100% - 44px)",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: "bold" }}
                                    >
                                      {option.name}
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {option.town}, {option.municipality}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Име на читалището или населено място"
                                error={!!errors.chitalishteName}
                                helperText={errors.chitalishteName || "Започнете да пишете, за да търсите..."}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {searchLoading ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />

                          <TextField
                            fullWidth
                            label="Детайли за местоположението / адреса"
                            name="locationDetails"
                            value={formData.locationDetails}
                            onChange={handleChange}
                            helperText="Улица, номер, населено място, община и т.н."
                            size={isSmall ? "small" : "medium"}
                            InputProps={{
                              startAdornment: (
                                <LocationOn
                                  sx={{mr: 1, color: "action.active" }}
                                />
                              ),
                            }}
                          />
                        </Stack>
                      </Box>

                      {/* Специфични полета според категорията */}
                      {formData.feedbackType === "website" && (
                        <TextField
                          fullWidth
                          label="Уебсайт или Facebook страница"
                          name="websiteInfo"
                          value={formData.websiteInfo}
                          onChange={handleChange}
                          helperText="Моля, въведете пълния URL адрес (напр. https://example.com)"
                          size={isSmall ? "small" : "medium"}
                          InputProps={{
                            startAdornment: (
                              <Public sx={{ mr: 1, color: "action.active" }} />
                            ),
                          }}
                        />
                      )}

                      {formData.feedbackType === "activity" && (
                        <TextField
                          fullWidth
                          label="Допълнителна информация за дейностите"
                          name="additionalInfo"
                          value={formData.additionalInfo}
                          onChange={handleChange}
                          helperText="Години, тип на дейностите, проекти и т.н."
                          size={isSmall ? "small" : "medium"}
                          InputProps={{
                            startAdornment: (
                              <DataObject
                                sx={{ mr: 1, color: "action.active" }}
                              />
                            ),
                          }}
                        />
                      )}

                      {/* Описание на обратната връзка */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            color: "#1a237e",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Info />
                          Описание на обратната връзка *
                        </Typography>

                        <TextField
                          fullWidth
                          multiline
                          rows={isSmall ? 4 : isMobile ? 5 : 6}
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          error={!!errors.message}
                          helperText={
                            errors.message ||
                            "Моля, опишете подробно вашата обратна връзка. Укажете конкретните грешки или допълнения, които искате да коригираме."
                          }
                          placeholder="Например: Читалището се намира на ул. 'Примерна' №5, а не на посочения адрес. Или: Имаме актуална информация за дейностите през 2024 г., която може да добавите."
                          size={isSmall ? "small" : "medium"}
                          sx={{
                            "& .MuiInputBase-root": {
                              textAlign: "justify",
                            },
                          }}
                        />
                      </Box>

                      {/* Изявление за източник на данните */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 2, sm: 3 },
                          borderRadius: 2,
                          bgcolor: "#fff8e1",
                          border: "1px solid #ffd54f",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1.5,
                            fontWeight: 600,
                            color: "#e65100",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Warning fontSize="small" />
                          Важно!
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            color: "#5d4037",
                            textAlign: "justify",
                          }}
                        >
                          Преди да подадете обратна връзка, моля проверете дали
                          същата информация не е налична в Публичния регистър на
                          народните читалища и на читалищните сдружения към
                          Министерството на културата.
                        </Typography>

                        <FormControl
                          error={!!errors.agreeTerms}
                          component="fieldset"
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="agreeTerms"
                                checked={formData.agreeTerms}
                                onChange={handleChange}
                                color="primary"
                                size={isSmall ? "small" : "medium"}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ textAlign: "justify" }}
                              >
                                Разбирам, че данните в картата са извлечени от
                                официалния регистър и съм проверил/а
                                информацията там преди да подам сигнал.
                              </Typography>
                            }
                          />
                          {errors.agreeTerms && (
                            <FormHelperText sx={{ ml: 0 }}>
                              {errors.agreeTerms}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Paper>

                      {/* Допълнителни настройки */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="contactPermission"
                            checked={formData.contactPermission}
                            onChange={handleChange}
                            color="primary"
                            size={isSmall ? "small" : "medium"}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Давам съгласие да се свържете с мен за допълнителни
                            въпроси относно моя сигнал
                          </Typography>
                        }
                      />

                      {/* Бутон за изпращане */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mt: 2,
                          pt: 2,
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          size={isSmall ? "medium" : "large"}
                          startIcon={
                            loading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <Send />
                            )
                          }
                          disabled={loading}
                          sx={{
                            background:
                              "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                            color: "white",
                            px: { xs: 4, md: 6 },
                            py: { xs: 1.5, md: 2 },
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            fontWeight: 600,
                            borderRadius: 3,
                            boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                            minWidth: { xs: "180px", sm: "200px" },
                            "&:hover": {
                              background:
                                "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
                              boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)",
                              transform: "translateY(-2px)",
                            },
                            "&:disabled": {
                              background: "#e0e0e0",
                              color: "#9e9e9e",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {loading ? "Изпращане..." : "Изпрати обратна връзка"}
                        </Button>
                      </Box>
                    </Stack>
                  </form>
                </CardContent>
              </Card>
            </Grid>

            {/* Дясна колона - инфо панел */}
            <Grid item xs={12} lg={4}>
              <Stack
                spacing={getSpacing()}
                sx={{ position: "sticky", top: 20 }}
              >
                {/* Избрана категория */}
                {selectedFeedbackType && (
                  <Card elevation={2} sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: "#1a237e" }}>
                        Избрана категория
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 2,
                          bgcolor: `${selectedFeedbackType.color}10`,
                          borderRadius: 2,
                          borderLeft: `4px solid ${selectedFeedbackType.color}`,
                        }}
                      >
                        <Box sx={{ color: selectedFeedbackType.color }}>
                          {selectedFeedbackType.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            {selectedFeedbackType.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {selectedFeedbackType.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Процес на обработка */}
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 3}}>
                    <Typography variant="h6" sx={{ mb: 2, color: "#1a237e" }}>
                      Процес на обработка
                    </Typography>
                    <Stack spacing={1.5}>
                      {[
                        { text: "Получаване на сигнала", icon: "📨" },
                        { text: "Верификация на информацията", icon: "🔍" },
                        { text: "Проверка с официални източници", icon: "📋" },
                        { text: "Прилагане на корекциите", icon: "✅" },
                        { text: "Уведомяване (ако е поискано)", icon: "📧" },
                      ].map((step, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: index === 0 ? "#e8f5e9" : "transparent",
                            border: index === 0 ? "1px solid #c8e6c9" : "none",
                          }}
                        >
                          <Typography sx={{ fontSize: "1.2rem" }}>
                            {step.icon}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: "justify",
                              flex: 1,
                            }}
                          >
                            {step.text}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Бързи връзки */}
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: "#1a237e" }}>
                      Полезни връзки
                    </Typography>
                    <Stack spacing={1}>
                      <Button
                        component="a"
                        href="https://mc.government.bg/bg/pages/register/chitalishta.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<Description />}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          py: 1.5,
                        }}
                      >
                        Публичен регистър на читалищата
                      </Button>

                      <Button
                        component={Link}
                        to="/faq"
                        startIcon={<Help />}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          py: 1.5,
                        }}
                      >
                        Често задавани въпроси
                      </Button>

                      <Button
                        component={Link}
                        to="/methodology"
                        startIcon={<Info />}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          py: 1.5,
                        }}
                      >
                        Методология на картата
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>

          {/* Бележка под формата */}
          <Card
            elevation={0}
            sx={{
              mt: { xs: 4, md: 6 },
              borderRadius: 3,
              background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
              border: "1px solid #ce93d8",
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={9}>
                  <Typography variant="h6" sx={{ mb: 1.5, color: "#7b1fa2" }}>
                    Вашият принос за развитието на картата
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#4a148c",
                      textAlign: "justify",
                      lineHeight: 1.7,
                    }}
                  >
                    Вашите сигнали ще ни помогнат постепенно да подобряваме
                    качеството на данните и да развиваме картата като споделен и
                    полезен ресурс за всички. Всеки подаден сигнал се разглежда
                    внимателно и допринася за по-точна и пълна информация за
                    всички читалища в България. Заедно създаваме точна и
                    актуална карта на културното наследство на страната.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3} sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      p: 2.5,
                      borderRadius: "50%",
                      bgcolor: "#7b1fa2",
                      color: "white",
                    }}
                  >
                    <Language sx={{ fontSize: 40 }} />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Бутони за навигация */}
          {/* <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: { xs: 4, md: 6 }, 
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <HomeRefreshButton />
            
            <Button
              component={Link}
              to="/methodology"
              variant="outlined"
              size={isSmall ? "medium" : "large"}
              startIcon={<Info />}
              sx={{
                borderColor: '#2e7d32',
                color: '#2e7d32',
                px: { xs: 3, md: 4 },
                py: { xs: 1, md: 1.5 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 600,
                borderRadius: 3,
                '&:hover': {
                  borderColor: '#1b5e20',
                  background: 'rgba(46, 125, 50, 0.04)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Методология
            </Button>

            <Button
              component={Link}
              to="/faq"
              variant="outlined"
              size={isSmall ? "medium" : "large"}
              startIcon={<Help />}
              sx={{
                borderColor: '#9c27b0',
                color: '#9c27b0',
                px: { xs: 3, md: 4 },
                py: { xs: 1, md: 1.5 },
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 600,
                borderRadius: 3,
                '&:hover': {
                  borderColor: '#7b1fa2',
                  background: 'rgba(156, 39, 176, 0.04)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              ЧЗВ
            </Button>
          </Box> */}
        </motion.div>
      </Container>

      {/* Snackbar за съобщения */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            maxWidth: { xs: "90vw", sm: "400px" },
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
          icon={
            snackbar.severity === "success" ? <CheckCircle /> : <ErrorIcon />
          }
        >
          <Typography variant="body2" sx={{ textAlign: "justify" }}>
            {snackbar.message}
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackComponent;
